import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import DropZone from 'components/dropzone/DropZone';
import { CheckboxSc } from 'components/final-form/CheckBox';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import { FormApi } from 'final-form';
import createDecorator from 'final-form-calculate';
import _ from 'lodash';
import moment from 'moment';
import React, { Component, ReactNode } from 'react';
import { FileRejection } from 'react-dropzone';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Button, Grid, Header, Icon, Popup, Table } from 'semantic-ui-react';
import axios from 'services/http';
import { getAllPcrTestResultsFromFile, importPctTestResults } from 'services/testResultImportServices';
import styled from 'styled-components';
import { CovidAnalyzerType } from 'ts-types/api.enums';
import {
  FieldValidationErrorDto,
  TestResultImportDto,
  TestResultImportSummaryDto,
  TestResultRecordDto
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const SummaryColumn = styled(Grid.Column)`
  .summary-container {
    display: flex;
    flex-direction: column;

    .summary-row {
      flex: 1 1 auto;
      margin-bottom: .5rem;
    }
  }
`;

const StyledSelectedInfo = styled.div`
  margin-top: 1rem;
`;

const StyledIcon = styled(Icon)`
  vertical-align: super;
`;


const ResultsPane = styled.div`
  flex: 0 0 auto;
  margin-right: 15px;
  min-width: 15rem;

  .results-header {
    font-weight: bold;
    font-size: 1.1rem;
    border-bottom: 1px solid #e5e5e5;
    margin-bottom: 0.5rem;
  }

  .results-container {
    display: grid;
    grid-template-columns: minmax(max-content, 1fr);
    row-gap: 0.35rem;
    column-gap: 1.2rem;

    .import-error {
      color: #575757;
    }
  }
`;

const TestResultTableContainer = styled.div`
  display: flex;
  flex-flow: column;
  height: 100%;
  width: 100%;

  .test-result-table {
    flex: 1 1 auto;
    min-height: 500px;
    width: 100%;
  }

  .action-buttons-row {
    flex: 0 0 auto;
    margin: 1rem 0 0;
  }
`;

interface Props extends RouteComponentProps<any>,
    AuthConsumerRenderProps,
    NotificationSystemProps,
    MessageSourceProps {

}

interface State {
  importSummary?: TestResultImportSummaryDto,
  testResultRecords: TestResultRecordDto[],
  analyzerFileExtensionMap: { [key: string]: string[] },
  analyzerFileExtOptions: DropdownOption[],
  numberOfImportedTestResults?: string,
  loadingTestResultImportData?: string,
  submitting: boolean,
  errorMessages: string[],
  fileExtension: string,
  cancelTokenSource: CancelTokenSource
}

class TestResultImportView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);
    const {getMessage} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const analyzerFileExtensionMap: { [key: string]: string[] } = {
      DMX: ["xls", "xlsx"],
      COBAS: ["csv"]
    };

    const analyzerFileExtensionLabels = Object.values(CovidAnalyzerType);
    const analyzerFileExtOptions: DropdownOption[] =
        analyzerFileExtensionLabels.map(ext => ({
          key: ext,
          value: ext,
          text: _.upperCase(ext)
        }));

    this.state = {
      submitting: false,
      testResultRecords: [],
      analyzerFileExtensionMap,
      analyzerFileExtOptions,
      loadingTestResultImportData: getMessage("loading"),
      errorMessages: [],
      fileExtension: "csv",
      cancelTokenSource
    }

  }

  componentDidMount() {
    this.setState({
      loadingTestResultImportData: undefined
    })
  };

  cancelImport = (form: FormApi) => {

    this.setErrorMessage();

    form.change("file", undefined);
    this.setState({
      testResultRecords: []
    })
  };

  setErrorMessage = (errorMessage?: string) => {

    const {errorMessages} = this.state;

    if (errorMessage) {

      const errMsgs = [...errorMessages];
      errMsgs.push(errorMessage);

      this.setState({
        errorMessages: errMsgs
      });
    } else {

      this.setState({
        errorMessages: []
      });
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.invalidInput,
      errorUtils.canNotImportTestResultsFromEmptyFile,
      errorUtils.fileWithIncompatibleDataInserted
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`));
        }
      });
    }

    if (!this.state.errorMessages.length) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`));
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };


  handleSubmit = (values: Partial<TestResultImportDto>, form: FormApi) => {

    if (this.state.submitting) {
      return;
    }

    const {analyzerFileExtensionMap, cancelTokenSource} = this.state;
    const {getMessage} = this.props;

    this.setState({
      testResultRecords: []
    })

    if (values.file && values.analyzerType) {

      this.setErrorMessage();

      this.setState({
        loadingTestResultImportData: getMessage("testResultImport.importing")
      });

      const fileExtension = this.getFileExtension(values.file);
      if (!analyzerFileExtensionMap[values.analyzerType].includes(fileExtension)) {
        this.setErrorMessage(getMessage("testResultImport.fileExtensionError"));
        this.setState({
          loadingTestResultImportData: undefined
        });
        return;
      }

      getAllPcrTestResultsFromFile(values, cancelTokenSource)
          .then((response) => {
            this.setState({
              importSummary: response,
              testResultRecords: response.testResultRecords,
              submitting: true
            });
          })
          .catch(error => this.handleError(error.response.data))
          .finally(() => {
            this.setState({
              loadingTestResultImportData: undefined,
              submitting: false
            });
          });
    }
  };

  analyzerDecorator = createDecorator({
    field: "file",
    updates: {
      analyzerType: () => {
        return this.state.fileExtension === "csv" ? CovidAnalyzerType.COBAS : CovidAnalyzerType.DMX
      }
    }
  });

  getFileExtension = (file: File): string => {
    const fileName = file.name;
    return fileName.substring(fileName.lastIndexOf('.') + 1);
  }

  hideMessage = (): void => {
    setTimeout(() => {
      this.setState({
        numberOfImportedTestResults: undefined
      });
    }, 3000);
  };

  handleImportApply = () => {
    const {testResultRecords, cancelTokenSource} = this.state;

    this.setState({
      submitting: true
    });

    let testResultsForImport: Array<TestResultRecordDto> = testResultRecords.filter(record => record.selected);
    const request: TestResultImportSummaryDto = {
      testResultRecords: testResultsForImport
    }

    importPctTestResults(request, cancelTokenSource)
        .then(response => {
          this.setState({
            numberOfImportedTestResults: response,
            testResultRecords: []
          });
          this.hideMessage();
        })
        .catch((e) => this.handleError(e.response.data))
        .finally(() => {
          this.setState({
            submitting: false
          });
        });
  };


  render = (): ReactNode => {
    const {getMessage} = this.props;
    const {testResultRecords, loadingTestResultImportData} = this.state;

    const initialValues: Partial<TestResultImportDto> = {analyzerType: CovidAnalyzerType.COBAS};

    const disableImport = !testResultRecords.filter(record => record.selected).length;

    return (
        <PraxStyledSegment style={{height: "100%"}}>
          <FinalForm
              onSubmit={this.handleSubmit}
              initialValues={initialValues}
              subscription={{values: true}}
              render={this.renderForm}
              decorators={[this.analyzerDecorator]}
          />
          <StyledViewTableRow id="testResultImportTable">
            {
                this.state.submitting &&
                <LoaderComponent message={getMessage("testResultImport.importing")}/>
            }
            {loadingTestResultImportData
                ? (<LoaderComponent message={loadingTestResultImportData}/>)
                : testResultRecords && testResultRecords.length > 0
                    ? <TestResultTableContainer id="testResultImportTable">
                      <div className="test-result-table">
                        {this.renderTestResultRecordsSegment()}
                      </div>
                      <div className="action-buttons-row">
                        <Button
                            type="button"
                            className="action-button"
                            onClick={() => this.handleImportApply()}
                            primary
                            compact
                            disabled={disableImport}
                        >
                          {getMessage("testResultImport.apply")}
                        </Button>
                      </div>
                    </TestResultTableContainer>
                    : null}
          </StyledViewTableRow>
        </PraxStyledSegment>
    );
  };

  renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {
    const {getMessage, getMessageWithNamedParams} = this.props;
    const {testResultRecords, numberOfImportedTestResults, analyzerFileExtOptions, errorMessages} = this.state;

    const uploadMessage = values.file ? values.file.name : getMessage("testResultImport.upload");

    let totalRows = testResultRecords.length;
    let matchedRows = testResultRecords.filter(record => record.matched).length;
    const selectedRows = testResultRecords.filter(record => record.selected).length;
    const disableCheckImportOrReset = !values.file;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid onKeyDown={(e: any) => this.onEnterKeyPressed(e, values, form)}>
                {errorMessages.length > 0 &&
                    <Grid.Row>
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessages.map(err => <div key={err}>{err}</div>)}
                      </StyledErrorMessage>
                    </Grid.Row>
                }
                <Grid.Row>
                  <Grid.Column>
                    <Header>{getMessage("testResultImport.header")}</Header>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    <Header style={{fontSize: "1em"}}>
                      {getMessage("testResultImport.file")}
                    </Header>
                  </Grid.Column>
                  <Grid.Column width={3}>
                    <Header style={{fontSize: "1em"}}>
                      {getMessage("testResultImport.analyzerType")}
                    </Header>
                  </Grid.Column>
                  {testResultRecords.length > 0 &&
                      <Grid.Column width={8}>
                        <Header style={{fontSize: "1em"}}>
                          {getMessage("testResultImport.summary")}
                        </Header>
                      </Grid.Column>
                  }
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    <DropZone
                        multiple={false}
                        message={uploadMessage}
                        onDropAccepted={(acceptedFiles: File[]) => this.onDropAccepted(acceptedFiles, form)}
                        onDropRejected={this.onDropRejected}
                    />
                  </Grid.Column>
                  <Grid.Column width={3}>
                    <Field
                        name="analyzerType"
                        component={Select}
                        options={analyzerFileExtOptions}
                        fluid
                    />
                  </Grid.Column>
                  {testResultRecords.length > 0 &&
                      <SummaryColumn width={8}>
                        <div className="summary-container">
                          <div className="summary-row">
                            {getMessage("testResultImport.totalRecords")}:
                            <strong>
                              &nbsp;{totalRows}
                            </strong>
                          </div>
                          <div className="summary-row">
                            {getMessage("testResultImport.matchedRecords")}:
                            <strong>
                              &nbsp;{matchedRows}
                            </strong>
                          </div>
                        </div>
                      </SummaryColumn>
                  }
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>
            <FormButtonsRow>
              <Button
                  type="submit"
                  primary
                  compact
                  disabled={disableCheckImportOrReset}
              >
                {getMessage("testResultImport.check")}
              </Button>

              <Button
                  compact
                  type="button"
                  onClick={() => this.cancelImport(form)}
                  disabled={disableCheckImportOrReset}
              >{getMessage("button.reset")}
              </Button>

              <span>
                {
                    numberOfImportedTestResults && !errorMessages.length &&
                    getMessageWithNamedParams("testResultImport.success",
                        {
                          numOfTestResults: numberOfImportedTestResults
                        })
                }
              </span>

              {
                  testResultRecords.length > 0 &&
                  <StyledSelectedInfo>
                    {`${getMessage("testResultImport.selectedRows")}: ${selectedRows} / ${matchedRows}`}
                  </StyledSelectedInfo>
              }
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
  };

  onEnterKeyPressed = (e: any, values: any, form: FormApi) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      this.handleSubmit(values, form);
    }
  };

  onDropAccepted = (acceptedFiles: File[], form: FormApi) => {

    this.setErrorMessage();
    this.setState({
      fileExtension: this.getFileExtension(acceptedFiles[0])
    })

    form.change("file", acceptedFiles[0]);
  };

  onDropRejected = (rejections: FileRejection[]) => {
    // TODO: Handle drop rejected
    console.log(rejections, "FILE REJECTIONS");
  };

  selectHeaderColumn = () => {

    const {testResultRecords} = this.state;
    const countSelectableTestResults = testResultRecords.filter(e => e.matched).length;
    const countSelectedTestResults = testResultRecords.filter(e => e.selected).length;
    const indeterminate = countSelectedTestResults > 0 && countSelectedTestResults < countSelectableTestResults;
    const noSelectedTestResults = countSelectedTestResults === 0 && countSelectableTestResults > 0;
    const allTestResultsSelected = countSelectableTestResults > 0 && countSelectedTestResults === countSelectableTestResults;

    return (
        <CheckboxSc
            checked={countSelectedTestResults > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedTestResults || indeterminate) {
                  let employees = [...testResultRecords];
                  const selectedTestResults = employees.map(e => {
                    if (e.matched) {
                      return {...e, selected: true};
                    }
                    return e;
                  });
                  this.setState({
                    testResultRecords: selectedTestResults
                  });
                }

                if (allTestResultsSelected) {
                  let employees = [...testResultRecords];
                  const selectedTestResults = employees.map(e => ({...e, selected: false}));
                  this.setState({
                    testResultRecords: selectedTestResults
                  });
                }

              }
            }
            disabled={countSelectableTestResults === 0}
        />
    )
  };

  selectTestResultRecord = (index: number) => {
    const {testResultRecords} = this.state;
    let testResult = testResultRecords[index];
    if (testResult && !testResult.errors && testResult.matched) {
      let testResults = [...testResultRecords];
      testResults[index].selected = !testResults[index].selected;
      this.setState({
        testResultRecords: testResults
      })
    }
  };

  renderTestResultRecordsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {testResultRecords} = this.state;

    return (
        <VirtualizedTable
            rowCount={testResultRecords.length}
            rowGetter={this.testResultRecordsGetter}
            rowRenderer={this.testResultRecordsRowRenderer}
            columns={[
              {
                width: 50,
                label: this.selectHeaderColumn(),
                dataKey: "index",
                cellRenderer: this.testResultSelectCellRenderer
              },
              {
                width: 300,
                label: (getMessage("patientCentral.overview.tableHeader.testKitCode")),
                dataKey: "testKitCode"
              },
              {
                width: 300,
                label: (getMessage("testSampleMethod")),
                dataKey: "sampleMethod",
                cellRenderer: this.sampleMethodCellRenderer
              },
              {
                width: 300,
                label: (getMessage("patientCentral.overview.tableHeader.testResult")),
                dataKey: "result",
                cellRenderer: this.resultCellRenderer
              },
              {
                width: 400,
                label: (getMessage("patientCentral.overview.tableHeader.testResultDate")),
                dataKey: "resultTimestamp",
                cellRenderer: this.resultTimestampCellRenderer
              },
              {
                width: 200,
                label: (getMessage("patientCentral.testResult.ct")),
                dataKey: "ct"
              }
            ]}
        />

    );
  };

  testResultRecordsGetter = ({index}: any) => {
    const {testResultRecords} = this.state;

    Object.assign(testResultRecords[index], {index: index + 1});

    return testResultRecords[index];
  };

  testResultRecordsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {testResultRecords} = this.state;

    const record = testResultRecords[index];

    let rowStyle = {...style};
    if (record.errors && record.errors.length > 0) {
      rowStyle = {...rowStyle, color: "rgb(198, 198, 203)"};
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={rowStyle}
            onDoubleClick={() => this.selectTestResultRecord(index)}
        >
          {columns}
        </div>
    );
  };

  testResultSelectCellRenderer = (data: any) => {

    const {rowData, rowIndex} = data;
    const {getMessage} = this.props;
    const {testResultRecords} = this.state;

    const testResult = testResultRecords[rowIndex];

    let showErrors = testResult.errors && testResult.errors.length;
    const parseErrors = testResult.errors;

    if (showErrors && parseErrors) {
      const trigger = (
          <Icon name="clipboard outline" fitted color="grey"/>
      );

      const content = (
          <ResultsPane>
            <div className="results-header">{getMessage("testResultImport.importError")}</div>
            <div className="results-container">
              <Table celled size="small" compact="very">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>{getMessage("testResultImport.field")}</Table.HeaderCell>
                    <Table.HeaderCell>{getMessage("testResultImport.errorCode")}</Table.HeaderCell>
                    <Table.HeaderCell>{getMessage("details")}</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {
                    parseErrors.map((e, i) => {
                      return (
                          <Table.Row key={i}>
                            <Table.Cell width={3}>
                              {e.field}
                            </Table.Cell>
                            <Table.Cell width={4}>
                              {getMessage(`error.${e.errorCode}`)}
                            </Table.Cell>
                            <Table.Cell width={6}>
                              {e.details}
                            </Table.Cell>
                          </Table.Row>
                      );
                    })
                  }
                </Table.Body>
              </Table>
            </div>
          </ResultsPane>
      );

      return (
          <div>
            <Popup
                trigger={trigger}
                popperModifiers={{
                  preventOverflow: {
                    boundary: document.getElementById("testResultImportTable")
                  }
                }}
                content={content}
                flowing
                on="hover"
                size="large"
                position="right center"
            />
          </div>
      );
    }

    if (!rowData.matched && (!rowData.warnings || !rowData.warnings.length)) {
      const canNotMatch = getMessage("testResultImport.warning.noMatch")
      return <Icon name="exclamation triangle" fitted color="orange" title={canNotMatch}/>
    }

    let warnings = [];

    if (rowData.warnings && rowData.warnings.length > 0) {
      const alreadySetWarning = rowData.warnings
          .find((warning: FieldValidationErrorDto) => warning.errorCode === errorUtils.testResultAlreadySet);

      const unknownAnalyzer = rowData.warnings
          .find((warning: FieldValidationErrorDto) => warning.errorCode === errorUtils.unknownAnalyzer);

      if (unknownAnalyzer) {
        warnings.push(getMessage(`error.${errorUtils.unknownAnalyzer}`));
      }

      if (alreadySetWarning) {
        warnings.push(getMessage(`error.${errorUtils.testResultAlreadySet}`));
        return <Icon name="exclamation triangle"
                     fitted
                     color="grey"
                     title={warnings.join('\r\n')}
        />;
      }

    }

    return (<>
          <CheckboxSc
              checked={rowData.selected}
              disabled={!rowData.matched}
              onChange={() => this.selectTestResultRecord(rowIndex)}
          />
          {warnings.length > 0 &&
              <StyledIcon name="exclamation triangle"
                          fitted
                          color="grey"
                          title={warnings.join('\r\n')}
              />
          }
        </>
    )
  };

  sampleMethodCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return <div>{getMessage(`covidTestSampleMethod.${cellData}`)}</div>
    }
    return "";
  };

  resultCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return <div>{getMessage(`enum.testResult.${cellData}`)}</div>
    }
    return "";
  };

  resultTimestampCellRenderer = ({cellData}: any) => {
    if (cellData) {
      const resultDateTimeTitle = moment(cellData).format('DD.MM.YYYY - HH:mm');
      const resultDateTime = moment(cellData).format('DD.MM.YYYY HH:mm');

      return (
          <div>
            <div className="date birth-date" title={resultDateTimeTitle}>{resultDateTime}</div>
          </div>
      )
    }
    return "";
  };

}

export default withRouterWorkaround(withAuthContext(withNotificationSystemContext(withMessages(TestResultImportView))));
